import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

/**
 * About page - Company story and philosophy (stealth mode)
 */
const About = () => {
  const values = [
    {
      title: 'Your People First',
      description: 'We design around the people who do the work. The AI fits the workflow — never the other way around.'
    },
    {
      title: 'Ship, Then Iterate',
      description: 'Working software in weeks. We get something real in your hands fast, then refine based on what your team actually needs.'
    },
    {
      title: 'You Own Everything',
      description: "Every line of code, every model, every deployment — it's yours. No lock-in, no dependencies on us."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div 
            className="absolute inset-0" 
            style={{
              backgroundImage: `radial-gradient(circle at 30% 30%, rgba(0, 229, 255, 0.15) 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        <div className="container relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-secondary hover:text-accent transition-colors mb-8"
            >
              ← Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-display font-bold gradient-text mb-6">
              About Z37
            </h1>
            <p className="text-secondary text-lg">
              AI systems that make your team unstoppable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-2xl font-display font-semibold text-primary mb-6">
                Why Z37?
              </h2>
              <div className="prose prose-invert prose-lg">
                <p className="text-secondary leading-relaxed mb-4">
                  We started Z37 because we kept seeing the same thing: talented teams doing important work, 
                  held back by processes that should have been automated years ago. The expertise was there. 
                  The technology existed. Nobody had connected the two.
                </p>
                <p className="text-secondary leading-relaxed mb-4">
                  The name comes from our philosophy: <strong className="text-primary">zero to one</strong> — 
                  taking an idea from nothing to a working system — then <strong className="text-primary">37</strong> — 
                  the non-obvious move that changes everything.
                </p>
                <p className="text-secondary leading-relaxed">
                  We're a small team of senior engineers. No juniors, no account managers, no layers. 
                  When you work with Z37, you work with the people who build the thing.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values/Philosophy */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-2xl font-display font-semibold text-primary mb-12 text-center"
            >
              How We Work
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <h3 className="text-lg font-display font-semibold text-accent mb-3">
                    {value.title}
                  </h3>
                  <p className="text-secondary text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
